import React, { useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import './AnalysisDisplay.css';
import { HandAnalysisData, StreetAnalysis, AnalysisSummary, OpponentRangeEstimation, EstimatedEV } from '../types'; // Import shared types

// --- Add a simple Card component --- 
const Card: React.FC<{ rank: string; suit: string }> = ({ rank, suit }) => {
    if (!rank || !suit) return null; // Handle potential missing card data
    const isRed = suit.toLowerCase() === 'h' || suit.toLowerCase() === 'd';
    const suitSymbol = {
        'h': '♥',
        'd': '♦',
        'c': '♣',
        's': '♠'
    }[suit.toLowerCase()] || suit;
    
    return (
        <div className="analysis-card">
            <div className={`analysis-card-inner ${isRed ? 'red-suit' : 'black-suit'}`}>
                <span className="analysis-card-rank">{rank.toUpperCase()}</span>
                <span className="analysis-card-suit">{suitSymbol}</span>
            </div>
        </div>
    );
};
// --- End Card component ---

// REMOVE Duplicate Interface Definitions
/*
interface AnalysisSummary { ... }
interface OpponentRangeEstimation { ... }
interface EstimatedEV { ... }
interface StreetAnalysis { ... }
interface HandAnalysisData { ... }
*/

interface AnalysisDisplayProps {
    analysis: HandAnalysisData | null;
    improvementFeedback: string;
    isImprovingAnalysis: boolean;
    onImprovementFeedbackChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onImproveAnalysisRequest: (handId: string) => void;
    // NEW: allow parent to control active tab
    activeTabProp?: string;
    onActiveTabChange?: (tab: string) => void;
}

// Helper function to get rating category (Updated for 1-10 scale)
const getRatingCategory = (rating: number): string => {
    if (rating >= 9) return 'Excellent'; // 9-10
    if (rating >= 7) return 'Good';      // 7-8
    if (rating >= 4) return 'Okay';       // 4-6
    if (rating >= 1) return 'Poor';       // 1-3
    return 'Unknown'; // Fallback for unexpected values
};

// Define street order helper (use optional chaining for safety)
const STREET_ORDER: Array<keyof NonNullable<HandAnalysisData['streets']>> = ['preflop', 'flop', 'turn', 'river'];

export const AnalysisDisplay: React.FC<AnalysisDisplayProps> = ({ 
    analysis,
    improvementFeedback,
    isImprovingAnalysis,
    onImprovementFeedbackChange,
    onImproveAnalysisRequest,
    activeTabProp,
    onActiveTabChange
}) => {
    const [internalActiveTab, setInternalActiveTab] = useState<string>('Overall');
    const activeTab = activeTabProp ?? internalActiveTab;
    const setActiveTab = (v: string) => {
        if (onActiveTabChange) onActiveTabChange(v);
        if (activeTabProp == null) setInternalActiveTab(v);
    };

    // Log props received RIGHT AWAY
    console.log("[AnalysisDisplay] Rendering. Received props:", { analysis, improvementFeedback, isImprovingAnalysis, activeTab });

    if (!analysis) {
        console.log("[AnalysisDisplay] No analysis prop, rendering 'No data'.");
        return <div className="analysis-display analysis-display--no-data">No analysis data available.</div>;
    }

    // --- MOVED UP: Define community card helpers and array before use ---
    const getCommunityCardsArray = (cardsString?: string): string[] => {
        if (!cardsString) return [];
        return cardsString.match(/.{1,2}/g) || []; // Split into pairs like ['Ad', '6h', ...]
    };

    const communityCardsArray = getCommunityCardsArray(analysis.communityCards);
    const flopCards = communityCardsArray.slice(0, 3);
    const turnCard = communityCardsArray.length >= 4 ? communityCardsArray[3] : null;
    const riverCard = communityCardsArray.length >= 5 ? communityCardsArray[4] : null;
    // --- END MOVED UP ---

    // --- REMOVE Street Filtering - Prepare data for ALL streets ---
    const allStreetTabData = STREET_ORDER.map(street => {
        const streetData = analysis.streets?.[street];
        const rating = streetData?.rating; // Revert to direct assignment
        const category = rating !== undefined ? getRatingCategory(rating) : 'Unknown'; // Revert to direct assignment
        const hasData = !!streetData;

        // Determine if actions exist for this street
        const actionsExist = hasData && streetData.action && Array.isArray(streetData.action) && streetData.action.length > 0;

        // Determine if the street *could* have occurred based on community cards
        let couldOccur = true;
        if (street === 'flop' && communityCardsArray.length < 3) couldOccur = false;
        if (street === 'turn' && communityCardsArray.length < 4) couldOccur = false;
        if (street === 'river' && communityCardsArray.length < 5) couldOccur = false;

        return {
            name: street,
            rating: rating,
            category: category,
            hasData: hasData, // Does any analysis object exist?
            hasActions: actionsExist && couldOccur, // Only count actions if street could occur
            couldOccur: couldOccur // Did the board reach this street?
        };
    });
    // --- END REMOVE Street Filtering ---

    // Log just before the final return
    const shouldShowImprovement = !!(analysis && analysis.id);
    console.log(`[AnalysisDisplay] Checking condition inside render. analysis.id: ${analysis?.id}. Should show improvement section: ${shouldShowImprovement}`);

    return (
        <div className="analysis-display">
            {/* Tabs styled like Hand Notes; add rating badges inside tabs */}
            <Tabs.Root value={activeTab} onValueChange={(v) => setActiveTab(v)} className="tabs-root">
                <Tabs.List className="tabs-list">
                    <Tabs.Trigger
                        value="Overall"
                        className={`tabs-trigger`}
                        disabled={!analysis.summary}
                        title="Overall"
                    >
                        Overall
                        {analysis.summary && (
                            <span className={`rating-badge overall`}>{getRatingCategory(analysis.summary.overallRating)}</span>
                        )}
                    </Tabs.Trigger>
                    {allStreetTabData.map(tabData => {
                        const title = tabData.name.charAt(0).toUpperCase() + tabData.name.slice(1);
                        const rating = tabData.rating;
                        let badgeClass = 'overall';
                        if (typeof rating === 'number') {
                            if (rating >= 9) badgeClass = 'excellent';
                            else if (rating >= 7) badgeClass = 'good';
                            else if (rating >= 4) badgeClass = 'okay';
                            else if (rating >= 1) badgeClass = 'poor';
                        }
                        const badgeText = typeof rating === 'number' ? getRatingCategory(rating) : 'N/A';
                        return (
                            <Tabs.Trigger
                                key={tabData.name}
                                value={tabData.name}
                                className={`tabs-trigger`}
                                title={title}
                            >
                                {title}
                                <span className={`rating-badge ${badgeClass}`}>{badgeText}</span>
                            </Tabs.Trigger>
                        );
                    })}
                </Tabs.List>
            </Tabs.Root>

            <div className="analysis-display__content">
                {/* Overall Content - Updated */} 
                {activeTab === 'Overall' && analysis.summary && (
                    <div className="analysis-display__overall">
                        <div className={`analysis-display__header analysis-display__header--${getRatingCategory(analysis.summary.overallRating).toLowerCase()}`}>
                            <h3 className="analysis-display__section-title">Overall Summary</h3>
                             <span className={`analysis-display__rating analysis-display__rating--${getRatingCategory(analysis.summary.overallRating).toLowerCase()}`}>
                                Rating: {analysis.summary.overallRating}/10 ({getRatingCategory(analysis.summary.overallRating)})
                             </span>
                         </div>
                        {/* --- ADDED: Display Hero Profit/Loss --- */}
                        {typeof analysis.heroProfitBB === 'number' && (
                            <p className="analysis-profit-loss" 
                               style={{ 
                                   color: analysis.heroProfitBB > 0 ? '#50cd89' : (analysis.heroProfitBB < 0 ? '#f64e60' : '#ffffff'),
                                   fontSize: '1.1rem',
                                   fontWeight: 'bold',
                                   margin: '0.5rem 0 1rem 0', // Adjust margin as needed
                                   padding: '0.5rem',
                                   background: 'rgba(0,0,0,0.1)',
                                   borderRadius: '4px',
                                   textAlign: 'center' 
                               }}
                            >
                                Result: {analysis.heroProfitBB > 0 ? '+' : ''}{analysis.heroProfitBB.toFixed(2)} BB
                            </p>
                        )}
                        {/* --- END: Display Hero Profit/Loss --- */}

                        {/* --- Show Question/Answer if present (moved here) --- */}
                        {analysis.questionAnswer && analysis.questionAnswer.question && analysis.questionAnswer.question.trim() !== '' && (
                            <div className="analysis-display__question-answer">
                                <h4 className="analysis-display__subsection-title">Question Answered</h4>
                                <p><strong>Q:</strong> {analysis.questionAnswer.question}</p>
                                <p><strong>A:</strong> {analysis.questionAnswer.answer}</p>
                            </div>
                        )}
                        <p><strong>Line Assessment:</strong> {analysis.summary.overallLineAssessment ?? 'N/A'}</p>
                        <p><strong>Hero Profile Tag:</strong> {analysis.summary.heroProfileTag ?? 'N/A'}</p>

                        <h4 className="analysis-display__subsection-title">Improvement Focus:</h4>
                        {analysis.summary.improvementFocus && analysis.summary.improvementFocus.length > 0 ? (
                            <ul className="analysis-display__list">
                                {analysis.summary.improvementFocus.map((rec, index) => (
                                    <li key={index}>{rec}</li>
                                ))}
                            </ul>
                        ) : (
                            <p>N/A</p>
                        )}
                        {/* Removed old fields like mainRecommendations, rangeAnalysisSummary */}

                        {/* --- ADDED: Tournament Context Section --- */}
                        {analysis.tournamentContextAnalysis && (
                            <div className="analysis-display__tournament-context">
                                <h4 className="analysis-display__subsection-title">Tournament Context Analysis</h4>
                                {analysis.tournamentContextAnalysis.icmImpact && (
                                    <p><strong>ICM Impact:</strong> {analysis.tournamentContextAnalysis.icmImpact}</p>
                                )}
                                {analysis.tournamentContextAnalysis.stageAdjustments && (
                                    <p><strong>Stage Adjustments:</strong> {analysis.tournamentContextAnalysis.stageAdjustments}</p>
                                )}
                                {analysis.tournamentContextAnalysis.bubbleFactorConsiderations && (
                                    <p><strong>Bubble Factor Considerations:</strong> {analysis.tournamentContextAnalysis.bubbleFactorConsiderations}</p>
                                )}
                                {analysis.tournamentContextAnalysis.payJumpConsiderations && (
                                    <p><strong>Pay Jump Considerations:</strong> {analysis.tournamentContextAnalysis.payJumpConsiderations}</p>
                                )}
                                {/* Handle case where the object exists but fields might be empty/null - unlikely with strict schema but good practice */}
                                {Object.values(analysis.tournamentContextAnalysis).every(val => !val) && (
                                     <p>No specific tournament context analysis provided.</p>
                                )}
                            </div>
                        )}
                        {/* --- END: Tournament Context Section --- */}
                    </div>
                )}

                {/* Street Content - Use allStreetTabData to check if activeTab is a street */}
                {allStreetTabData.some(tab => tab.name === activeTab) && (
                    <div className="analysis-display__street">
                        {(() => {
                            const streetName = activeTab as keyof NonNullable<HandAnalysisData['streets']>;
                            // Find the corresponding tab data for the active street
                            const currentTabData = allStreetTabData.find(tab => tab.name === streetName);
                            const streetData = analysis.streets?.[streetName]; // Get potential data

                            // Check if the street could have occurred
                            if (!currentTabData?.couldOccur) {
                                return <p>This street did not occur in the hand.</p>;
                            }

                            // Check if analysis data exists for this street
                            if (!streetData || !currentTabData?.hasData) {
                                return <p>No analysis data available for this street.</p>;
                            }

                            // Check if actions exist for this street
                            if (!currentTabData?.hasActions) {
                                return <p>No actions occurred on this street.</p>;
                            }

                            // --- If we pass all checks, render the content ---
                            const ratingCategory = currentTabData.category;

                            // Determine which community cards to show for this street
                            let boardToShow: string[] = [];
                            let boardTitle = "Board";
                            if (streetName === 'flop') {
                                boardToShow = flopCards;
                                boardTitle = "Flop";
                            } else if (streetName === 'turn' && turnCard) {
                                // Show flop + turn
                                boardToShow = flopCards.concat([turnCard]); 
                                boardTitle = "Board after Turn";
                            } else if (streetName === 'river' && riverCard) {
                                // Show flop + turn + river
                                boardToShow = flopCards.concat(turnCard ? [turnCard] : [], [riverCard]);
                                boardTitle = "Final Board";
                            } // Preflop shows no board cards

                            return (
                                <>
                                    <div className={`analysis-display__header analysis-display__header--${ratingCategory.toLowerCase()}`}>
                                        <h3 className="analysis-display__title">
                                            {streetName.charAt(0).toUpperCase() + streetName.slice(1)}
                                        </h3>
                                        {/* Conditionally render rating */}
                                        {currentTabData?.hasActions && (
                                            <span className={`analysis-display__rating analysis-display__rating--${ratingCategory.toLowerCase()}`}>
                                                Rating: {streetData.rating}/10 ({ratingCategory})
                                            </span>
                                        )}
                                    </div>

                                    {/* --- COMBINED: Hero and Community Cards --- */}
                                    <div className="hero-and-board-display">
                                        {/* Always show Hero Cards if available */} 
                                        {analysis.heroCards && (
                                            <div className="hero-cards-section">
                                                <h4 className="analysis-display__subsection-title">Hero Cards:</h4>
                                                <div className="card-container">
                                                    <Card rank={analysis.heroCards.holeCard1[0]} suit={analysis.heroCards.holeCard1[1]} />
                                                    <Card rank={analysis.heroCards.holeCard2[0]} suit={analysis.heroCards.holeCard2[1]} />
                                                </div>
                                            </div>
                                        )}
                                        {/* Show Community Cards for the street */} 
                                        {boardToShow.length > 0 && (
                                            <div className="community-cards-section">
                                                <h4 className="analysis-display__subsection-title">{boardTitle}:</h4>
                                                <div className="card-container">
                                                    {boardToShow.map(card => (
                                                        <Card key={card} rank={card[0]} suit={card[1]} />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {/* --- END COMBINED: Hero and Community Cards --- */}

                                    {/* --- NEW Wrapper for Action Sequence and Pot Size --- */}
                                    <div className="action-pot-container">

                                        {/* --- Display Structured Actions --- */}
                                        <div className="analysis-display__action-sequence">
                                            <h4 className="analysis-display__subsection-title">Action Sequence:</h4>
                                            {streetData.action.map((actionItem: { player: string; type: string; amount?: number; isAllIn?: boolean }, index: number) => {
                                                // Define rating based on the actionItem if it exists, otherwise use streetData rating as fallback?
                                                // For now, let's assume rating might be added to actionItem later, but use streetData.rating as primary
                                                const ratingCategory = getRatingCategory(streetData.rating); // Use street rating for class application
                                                const ratingValue = streetData.rating; // Use street rating for now

                                                return (
                                                    <div key={index} className="analysis-action-item"> 
                                                        <div className="action-details">
                                                            <span className="action-player">{actionItem.player || 'Unknown Player'}:</span>
                                                            <span className="action-type">{actionItem.type || 'Unknown Action'}</span>
                                                            {actionItem.amount != null && (
                                                                <span className="action-amount">${actionItem.amount}</span>
                                                            )}
                                                            {actionItem.isAllIn && (
                                                                <span className="action-all-in">(AI)</span> // Simple indicator for All-in
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {/* Street feedback remains within action sequence section for now */}
                                            {streetData.analysis && (
                                                <div className="street-feedback">
                                                    <h5 className="analysis-display__subsection-title">Street Feedback:</h5>
                                                    <p>{streetData.analysis}</p>
                                                </div>
                                            )}
                                        </div>
                                        {/* --- End Structured Actions --- */}

                                        {/* --- Pot Size Info (Now alongside Action Sequence) --- */}
                                        {(streetData.potSizeAtStart !== undefined || streetData.potSizeAtEnd !== undefined) && (
                                            <div className="pot-size-info-outside"> 
                                                <h4 className="analysis-display__subsection-title">Pot Size:</h4>
                                                <div className="pot-size-values"> 
                                                    {streetData.potSizeAtStart !== undefined && (
                                                        <span className="detail-tag pot-size-tag">Start: ${streetData.potSizeAtStart}</span>
                                                    )}
                                                    {streetData.potSizeAtStart !== undefined && streetData.potSizeAtEnd !== undefined && (
                                                        <span className="pot-size-separator"> / </span>
                                                    )}
                                                    {streetData.potSizeAtEnd !== undefined && (
                                                        <span className="detail-tag pot-size-tag">End: ${streetData.potSizeAtEnd}</span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                        {/* --- End Pot Size Info --- */}

                                    </div>
                                    {/* --- END Wrapper --- */}

                                    {/* --- Display Street-Level Details (Grid Section) --- */}
                                    <div className="street-details-section">
                                        {/* --- NEW ORDER --- */}

                                        {/* 1. Learning Tips (Moved to top) */}
                                        {streetData.learningTips && streetData.learningTips.length > 0 && (
                                            <div className="subsection learning-tips">
                                                <h4 className="analysis-display__subsection-title">Learning Tips:</h4>
                                                <ul className="analysis-display__list">
                                                    {streetData.learningTips.map((tip, index) => (
                                                        <li key={index}>{tip}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {/* 2. Opponent Range Estimation */}
                                        {streetData.opponentRangeEstimation && streetData.opponentRangeEstimation.handClassSummary && (
                                            <div className="subsection opponent-range">
                                                <h4 className="analysis-display__subsection-title">Opponent Range (Est.):</h4>
                                                <div className="opponent-range-tags-container">
                                                    {streetData.opponentRangeEstimation.handClassSummary
                                                        .split(/[,;]+/) // Split by comma or semicolon
                                                        .map(item => item.trim()) // Trim whitespace
                                                        .filter(item => item) // Remove empty strings
                                                        .map((tag, index) => (
                                                            <span key={index} className="detail-tag opponent-range-tag">{tag}</span>
                                                        ))
                                                    }
                                                </div>
                                                {/* Optional: Add more details like likelyHands list */}
                                            </div>
                                        )}

                                        {/* 3. Frequency Recommendation (Renamed from GTO) */}
                                        {streetData.gtoOptimalPlay && (
                                            <div className="subsection gto-optimal-play">
                                                <h4 className="analysis-display__subsection-title">Frequency Recommendation:</h4> {/* Renamed title */}
                                                {typeof streetData.gtoOptimalPlay === 'string' ? (
                                                    <p>{streetData.gtoOptimalPlay}</p> // Handle old string format if necessary
                                                ) : Array.isArray(streetData.gtoOptimalPlay) && streetData.gtoOptimalPlay.length > 0 ? (
                                                    <div className="gto-actions-list">
                                                        {streetData.gtoOptimalPlay
                                                            .slice()
                                                            .sort((a: any, b: any) => {
                                                                const af = typeof a?.frequency === 'number' ? a.frequency : parseFloat(String(a?.frequency));
                                                                const bf = typeof b?.frequency === 'number' ? b.frequency : parseFloat(String(b?.frequency));
                                                                const an = Number.isFinite(af) ? af : 0;
                                                                const bn = Number.isFinite(bf) ? bf : 0;
                                                                return bn - an; // descending
                                                            })
                                                            .map((gtoAction, index) => {
                                                            // Log the frequency value being used
                                                            console.log(`Rendering GTO Bar for ${gtoAction.action}: Frequency = ${gtoAction.frequency}`);
                                                            return (
                                                                <div key={index} className="gto-action-item">
                                                                    <div className="gto-action-info">
                                                                        <span className="gto-action-name">{gtoAction.action}</span>
                                                                        <span className="gto-action-percentage">
                                                                            {gtoAction.frequency}%
                                                                        </span>
                                                                    </div>
                                                                    <div className="gto-frequency-bar-container">
                                                                        <div
                                                                            className="gto-frequency-bar"
                                                                            style={{ width: `${gtoAction.frequency}%` }}
                                                                        ></div>
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <p>N/A</p>
                                                )}
                                            </div>
                                        )}

                                        {/* 4. Alternative Exploitative Line (Order adjusted) */}
                                        {streetData.alternativeExploitativeLine && (
                                            <div className="subsection alternative-exploitative-line">
                                                <h4 className="analysis-display__subsection-title">Alternative Exploitative Line:</h4>
                                                <p>{streetData.alternativeExploitativeLine}</p>
                                            </div>
                                        )}

                                        {/* 5. Combined Hand Class, Equity, EV (Order adjusted) */}
                                        {(streetData.handClass || streetData.equityEstimation || streetData.estimatedEV) && (
                                            <div className="subsection hand-equity-ev-combined">
                                                <h4 className="analysis-display__subsection-title">Hand/Equity/EV Details:</h4>
                                                {/* Hand Class */}
                                                {streetData.handClass && (
                                                    <div className="detail-item hand-class-detail">
                                                        <p><strong>Hand Class:</strong> <span className="detail-tag hand-class-tag">{streetData.handClass}</span></p>
                                                    </div>
                                                )}
                                                {/* Equity Estimation */}
                                                {streetData.equityEstimation && (
                                                    <div className="detail-item equity-detail">
                                                         <p><strong>Equity (Est.):</strong> <span className="detail-tag equity-tag">{streetData.equityEstimation}</span></p>
                                                    </div>
                                                )}
                                                {/* Estimated EV */}
                                                {streetData.estimatedEV && (
                                                    <div className="detail-item ev-detail">
                                                        <p><strong>EV:</strong> <span className="detail-tag ev-tag">{`${streetData.estimatedEV.action}: ${streetData.estimatedEV.value.toFixed(2)} (Confidence: ${streetData.estimatedEV.confidence})`}</span></p>
                                                    </div>
                                                )}
                                             </div>
                                        )}

                                        {/* --- Render SPR LAST --- */}
                                        {streetData.spr != null && (
                                            <div className="subsection spr-value">
                                                <h4 className="analysis-display__subsection-title">SPR (Est.):</h4>
                                                <p><span className="detail-tag spr-tag">{streetData.spr.toFixed(2)}</span></p>
                                            </div>
                                        )}
                                        {/* --- End SPR Section --- */}

                                    </div> {/* End .street-details-section */}
                                </>
                            );
                        })()}
                    </div>
                )}
            </div>

            {/* Improvement Feedback Section removed on replayer page */}
        </div>
    );
}; 